import React from 'react';
import MetricSection from './MetricSection';
import SourceFooter from './SourceFooter';
import TimeSeriesChart from './TimeSeriesChart';
import { ChartPanel, datasetById, MetricCallout } from './SectionElements';
import {
    OpenRouterLeaderboard,
    OpenRouterTokenChart,
    totalForWeek,
    useOpenRouterRankings,
} from './OpenRouterRankings';

const UsageMonetizationSection = ({ datasets, range, isZh = false }) => {
    const rankings = datasetById(datasets, 'openrouter_rankings');
    const revenue = datasetById(datasets, 'frontier_lab_revenue');
    const {
        chartData,
        chartCachedAt,
        chartStatus,
        leaderboard,
        leaderboardStatus,
        leaderboardView,
        setLeaderboardView,
    } = useOpenRouterRankings(rankings);
    const firstWeeklyTokens = totalForWeek(chartData[0]);
    const latestWeeklyTokens = totalForWeek(chartData[chartData.length - 1]);
    const weeklyGrowth = firstWeeklyTokens ? ((latestWeeklyTokens / firstWeeklyTokens) - 1) * 100 : 0;
    const latestOpenAI = [...revenue.series].reverse().find((point) => point.company === 'OpenAI');
    const latestAnthropic = [...revenue.series].reverse().find((point) => point.company === 'Anthropic');

    return (
        <MetricSection
            id="usage-monetization"
            index={3}
            title="Usage and Monetization"
            titleZh="真实使用与商业化"
            summary="Track OpenRouter's weekly token expansion and live model leaderboard alongside source-labeled private-company revenue events."
            summaryZh="先看 OpenRouter 周度 Token 总量如何增长、哪些模型正在获得真实使用，再对照前沿模型公司的收入兑现。"
            isZh={isZh}
        >
            <div className="ai-dev-callout-row">
                <MetricCallout value={`${(latestWeeklyTokens / 1e12).toFixed(1)}T`} label={isZh ? 'OpenRouter 最新周 Token' : 'Latest OpenRouter weekly tokens'} note="prompt + completion" />
                <MetricCallout value={`+${weeklyGrowth.toFixed(0)}%`} label={isZh ? '图表区间 Token 增长' : 'Token growth across chart range'} note={`${chartData[0]?.x || ''} → ${chartData[chartData.length - 1]?.x || ''}`} />
                <MetricCallout value={`$${latestOpenAI.value.toFixed(0)}B`} label="OpenAI annualized" note={`${latestOpenAI.date} · ${latestOpenAI.evidenceType.replaceAll('_', ' ')}`} />
                <MetricCallout value={`$${latestAnthropic.value.toFixed(0)}B`} label="Anthropic annualized" note={`${latestAnthropic.date} · ${latestAnthropic.evidenceType.replaceAll('_', ' ')}`} />
            </div>
            <ChartPanel className="ai-dev-panel--primary ai-dev-openrouter-panel" eyebrow="OPENROUTER · TOP MODELS" title={isZh ? '周度 Token 总量与热门模型构成' : 'Weekly tokens and top-model mix'} note={isZh ? '过去 52 周 · Linear / Log' : 'Trailing 52 weeks · Linear / Log'}>
                <OpenRouterTokenChart data={chartData} cachedAt={chartCachedAt} status={chartStatus} isZh={isZh} />
                <SourceFooter dataset={rankings} isZh={isZh} />
            </ChartPanel>
            <ChartPanel eyebrow="OPENROUTER · LLM LEADERBOARD" title={isZh ? '热门模型排行榜' : 'Popular model leaderboard'} note={isZh ? '按 Token 用量排序' : 'Ranked by token usage'}>
                <OpenRouterLeaderboard
                    rows={leaderboard}
                    status={leaderboardStatus}
                    view={leaderboardView}
                    onViewChange={setLeaderboardView}
                    isZh={isZh}
                />
            </ChartPanel>
            <ChartPanel className="ai-dev-panel--primary" eyebrow={isZh ? '商业化兑现' : 'MONETIZATION'} title={isZh ? '前沿模型公司年化收入' : 'Frontier-lab annualized revenue'} note={isZh ? '独立整行 · ARR 与年化运行率保留原口径' : 'full-width · reported metric types retained'}>
                <TimeSeriesChart dataset={revenue} range={range} seriesKey="company" ariaLabel="Frontier lab annualized revenue" scale="log" valueFormatter={(value) => `$${value.toFixed(value < 1 ? 1 : 0)}B`} />
                <SourceFooter dataset={revenue} isZh={isZh} />
            </ChartPanel>
        </MetricSection>
    );
};

export default UsageMonetizationSection;
